import * as z from "zod";

import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";

import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "../../../providers/form";
import { useTranslation } from "react-i18next";
import i18next from "i18next";
import InputField from "widgets/shared/inputs/inputField";
import { useNavigate } from "react-router-dom";
import GenericButton from "widgets/controls/buttons/generic";

// Schema
const AlertsActionsSchema = z.object({
  comment: z.string().min(1, {
    message: i18next.t("zod.error.comment"),
  }),
});

export type AlertsActionsFormType = z.infer<typeof AlertsActionsSchema>;

// Props
type AlertsActionsFormProps = {
  submit: (data: AlertsActionsFormType) => void;
  onClose: () => void;
  defaultValues?: AlertsActionsFormType;
  values?: AlertsActionsFormType;
  loading?: boolean;
};

const AlertsActionsForm = ({ submit, onClose, defaultValues, values, loading }: AlertsActionsFormProps) => {
  const form = useForm<AlertsActionsFormType>({
    resolver: zodResolver(AlertsActionsSchema),
    defaultValues: defaultValues || {
      comment: "",
    },
    values: values || {
      comment: "",
    },
  });

  const { t } = useTranslation();
  const { errors, isDirty } = form.formState;

  const onSubmit = async (formData: AlertsActionsFormType) => {
    try {
      await submit(formData);
    } catch (error) {}
  };

  const clearErrors = () => {
    form.clearErrors();
  };

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
        <div className="space-y-3">
          <FormField
            control={form.control}
            name="comment"
            rules={{ required: true }}
            render={({ field }) => (
              <FormItem>
                <FormLabel>{t("form.input.comment")}</FormLabel>
                <FormControl>
                  <InputField
                    placeholder={t("form.placeholder.comment")}
                    id="comment"
                    type="text"
                    state={errors.comment ? "error" : undefined}
                    onFocus={clearErrors}
                    color="secondary"
                    {...field}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
        </div>

        <div className="flex justify-end py-2">
          <div className="flex gap-2">
            <GenericButton
              onClick={onClose}
              text={t("button.action.cancel")}
              size="md"
              variant="ghost"
            />

            <GenericButton
              type="submit"
              text={t("button.action.ok")}
              disabled={!isDirty}
              loading={loading || false}
              size="md"
            />
          </div>
        </div>
      </form>
    </Form>
  );
};

export default AlertsActionsForm;
